/** @jsxImportSource @emotion/react */
import { Buttons, Dialogs } from '@zip/business-components';
import { useAuth0, useMerchantData, useSnackbar } from 'contexts';
import { FeatureFlag, MfaEnrollmentStatus, PageAction } from 'enums';
import { useFeatureFlagEnabled } from 'hooks';
import { useEffect, useRef, useState } from 'react';
import { logEvent, useMfaFunctions } from 'utils';
import * as styles from './CompleteMfa.styles';

function CompleteMfaDialogModule(): JSX.Element {
  const { logout } = useAuth0();
  const Snackbar = useSnackbar();
  const { acceptMfaAsync } = useMfaFunctions();
  const { isAdmin, currentUserMfaEnrollmentStatus } = useMerchantData();
  const userMfaEnrollmentStatusCached = useRef<MfaEnrollmentStatus>(
    currentUserMfaEnrollmentStatus
  );

  const completeMfaDialogTitle = 'Complete 2-step verification';
  const completeMfaDialogContent =
    'To strengthen your account security, you’ll be signed out and asked to register your phone number.';

  const [shouldOpen, setShouldOpen] = useState<boolean>(false);

  const selectiveMfaEnabled = useFeatureFlagEnabled(
    FeatureFlag.EnableSelectiveMFA
  );

  useEffect(() => {
    if (
      isAdmin &&
      currentUserMfaEnrollmentStatus === MfaEnrollmentStatus.Invited &&
      userMfaEnrollmentStatusCached.current === MfaEnrollmentStatus.Disabled
    ) {
      setShouldOpen(true);
    }
    userMfaEnrollmentStatusCached.current = currentUserMfaEnrollmentStatus;
  }, [currentUserMfaEnrollmentStatus]);

  const handleClick = async (): Promise<void> => {
    logEvent(PageAction.mfaCompleteDialogClick, {
      method: 'complete-mfa-popup',
    });
    try {
      await acceptMfaAsync();
      logout();
    } catch (err) {
      setShouldOpen(false);
      Snackbar.error('Something went wrong');
    }
  };

  return (
    selectiveMfaEnabled && (
      <Dialogs.Basic
        open={shouldOpen}
        showClose
        onClose={(): void => setShouldOpen(false)}
        css={styles.completeMfaDialog}
      >
        <div css={styles.completeMfaDialogContent}>
          <div css={styles.completeMfaDialogTitle}>
            {completeMfaDialogTitle}
          </div>
          <div css={styles.completeMfaDialogText}>
            {completeMfaDialogContent}
          </div>
          <div css={styles.completeMfaDialogButtonRegion}>
            <Buttons.Primary
              onClick={handleClick}
              css={styles.completeMfaDialogSignOutButton}
            >
              Sign out
            </Buttons.Primary>
          </div>
        </div>
      </Dialogs.Basic>
    )
  );
}

export default CompleteMfaDialogModule;
